import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  MessageCircle,
  Send,
  User,
  Briefcase,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientMessages() {
  const { portalInfo } = useOutletContext() || {};
  const firmName = portalInfo?.workspace_name || 'them';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/messages`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          message_id: data.message_id,
          sender: 'client',
          sender_name: 'You',
          text: data.text,
          created_at: data.created_at,
        }]);
        setText('');
      }
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-messages">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Messages</h1>
        <p className="text-muted-foreground text-sm mt-1">Communicate directly with {firmName}. Messages are private and secure.</p>
      </div>

      <Card className="flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}>
        {/* Messages Area */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-muted-foreground font-medium">No messages yet</p>
                <p className="text-xs text-muted-foreground mt-1">Send a message to start the conversation</p>
              </div>
            </div>
          ) : (
            messages.map(msg => {
              const isClient = msg.sender === 'client';
              return (
                <div key={msg.message_id} className={cn("flex gap-2", isClient ? "justify-end" : "justify-start")}>
                  {!isClient && (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  )}
                  <div className={cn(
                    "max-w-[75%] rounded-2xl px-4 py-2.5",
                    isClient
                      ? "bg-emerald-600 text-white rounded-br-md"
                      : "bg-muted rounded-bl-md"
                  )}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={cn("text-[11px] font-medium", isClient ? "text-emerald-100" : "text-muted-foreground")}>
                        {isClient ? 'You' : msg.sender_name || firmName}
                      </span>
                      <span className={cn("text-[10px]", isClient ? "text-emerald-200" : "text-muted-foreground/60")}>
                        {new Date(msg.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className={cn("text-sm leading-relaxed", isClient ? "text-white" : "text-foreground")}>
                      {msg.text}
                    </p>
                  </div>
                  {isClient && (
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </CardContent>

        {/* Input Area */}
        <div className="border-t p-3 flex items-center gap-2">
          <Input
            placeholder="Type a message..."
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
            data-testid="message-input"
          />
          <Button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            size="icon"
            className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
